/* =============================================================================
 *  useYesterdayAdsKpi — yesterday's Google Ads KPI for OUR own account.
 *
 *  Reads the cached daily store (googleAdsAccounts/{cid}/dailyMetrics/{month},
 *  month-bucketed → unpackMonthDoc) for the previous day and aggregates the row.
 *  "Our" account = the googleAdsAccounts doc whose label/name contains
 *  "web construct"; falls back to the configured defaultCustomerId. One bounded
 *  read on mount; degrades to null on any miss (no data / not connected).
 * ============================================================================= */

import { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, getFirestore, query as fsQuery, where } from 'firebase/firestore';
import {
  addDaysStr,
  aggregateDaily,
  todayLocal,
  unpackMonthDoc,
} from '../../pages/marketing/googleAdsApi';

// Web Construct's own Google Ads account (CID 960-753-8710), pinned.
const OUR_ADS_CID = '9607538710';

export interface AdsKpi {
  date: string;
  cost: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversionsValue: number;
  monthSpend: number; // month-to-date spend (this calendar month through today)
  accountName: string;
}

async function resolveOurAccount(db: ReturnType<typeof getFirestore>): Promise<{ cid: string; name: string }> {
  // Pinned to our own account; read its label/name for display (best-effort).
  let name = 'Web Construct';
  try {
    const snap = await getDoc(doc(db, 'googleAdsAccounts', OUR_ADS_CID));
    if (snap.exists()) { const d: any = snap.data() || {}; name = d.label || d.name || name; }
  } catch { /* keep default name */ }
  return { cid: OUR_ADS_CID, name };
}

export function useYesterdayAdsKpi(): { kpi: AdsKpi | null; loading: boolean } {
  const [kpi, setKpi] = useState<AdsKpi | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore();
        const { cid, name } = await resolveOurAccount(db);
        if (!cid) { if (!cancelled) { setKpi(null); setLoading(false); } return; }

        const today = todayLocal();
        const yesterday = addDaysStr(today, -1);
        const monthStart = `${today.slice(0, 7)}-01`; // 1st of this calendar month
        // Months we need: yesterday's + this month's (identical except on the 1st).
        const months = Array.from(new Set([yesterday.slice(0, 7), today.slice(0, 7)]));
        const snap = await getDocs(fsQuery(
          collection(db, 'googleAdsAccounts', cid, 'dailyMetrics'),
          where('month', 'in', months),
        ));
        const docs = snap.docs.map((d) => d.data());
        const yAgg = aggregateDaily(docs.flatMap((d) => unpackMonthDoc(d, yesterday, yesterday)) as any);
        // Month-to-date spend = sum of this month's daily costs through today.
        const mAgg = aggregateDaily(docs.flatMap((d) => unpackMonthDoc(d, monthStart, today)) as any);
        if (!cancelled) {
          setKpi({
            date: yesterday,
            cost: yAgg.cost, impressions: yAgg.impressions, clicks: yAgg.clicks,
            conversions: yAgg.conversions, conversionsValue: yAgg.conversionsValue,
            monthSpend: mAgg.cost,
            accountName: name,
          });
          setLoading(false);
        }
      } catch (e) {
        console.warn('[useYesterdayAdsKpi] failed', e);
        if (!cancelled) { setKpi(null); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { kpi, loading };
}
